import React, { useState } from 'react';
import { Box, Button, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { brand } from '../../theme';

/** Multicam in four steps, for the set-up page and the library's "new" note. */
export const MULTICAM_STEPS: ReadonlyArray<{ title: string; text: string }> = [
  {
    title: 'Add your cameras',
    text: 'Up to six recordings of the same moment; the master sets the timeline. Recorded the sound on its own recorder or mixer? Add that file too: it becomes the set’s sound. Say who’s on each camera: on Pro Max, the transcript names who’s talking.',
  },
  {
    title: 'They line up by sound',
    text: 'PithPlay listens to each camera, and to a separate sound recording, and lines them up with the master, usually in seconds. No clap and no typing in offsets. If an angle’s sound doesn’t match, it still plays with the others: line it up by picture, on a moment both cameras show. Want separate clips to play one after another instead? Choose Join clips.',
  },
  {
    title: 'Direct the edit',
    text: 'The studio opens with every angle on screen: press 1 to 6 as it plays to pick the camera (Live cut). On Pro Max, PithPlay can also suggest cuts from who’s speaking, and you can select words in the transcript to remove them, make them a short, or add a note. Or leave the camera choices to your editor.',
  },
  {
    title: 'Your editor gets it ready to edit',
    text: 'Every camera in sync, your cuts and notes, and a rough cut for Premiere Pro, DaVinci Resolve or Final Cut Pro. On Pro Max, also a transcript of the conversation with what you marked to remove and to make into shorts.',
  },
];

export const MULTICAM_FILES_NOTE =
  'MP4, MOV or WebM videos, up to 10 GB each on Pro and 25 GB on Pro Max (1 GB on Free), plus an optional WAV, MP3, M4A, AAC or AIFF from your recorder. If your connection drops during an upload, choose the same file again and it carries on where it stopped. Files that recorded sound line up by themselves; line up any other file by picture.';

/** The steps and the file note, with no frame of its own. */
export const MulticamHowItWorks: React.FC = () => (
  <Box>
    <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gap: 1.5 }}>
      {MULTICAM_STEPS.map((step, index) => (
        <Box component="li" key={step.title} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <Box
            aria-hidden="true"
            sx={{
              flexShrink: 0,
              width: 24,
              height: 24,
              mt: '1px',
              borderRadius: '50%',
              bgcolor: brand.ivory,
              border: `1px solid ${brand.border}`,
              color: brand.copperDark,
              fontSize: 12,
              fontWeight: 700,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            {index + 1}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {step.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {step.text}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
      <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
        Files:{' '}
      </Box>
      {MULTICAM_FILES_NOTE}
    </Typography>
  </Box>
);

const OPEN_KEY = 'pithplay.multicam.howItWorks';

// Browser storage is a convenience: private windows and blocked storage throw.
const readOpen = (): boolean => {
  try {
    return window.localStorage.getItem(OPEN_KEY) !== 'closed';
  } catch {
    return true;
  }
};

const saveOpen = (open: boolean) => {
  try {
    window.localStorage.setItem(OPEN_KEY, open ? 'open' : 'closed');
  } catch {
    // Not remembered; it opens again next time.
  }
};

/**
 * "How multicam works" as a panel that folds away. It starts open, and stays
 * closed once someone closes it in this browser.
 */
export const MulticamHowItWorksNote: React.FC = () => {
  const [open, setOpen] = useState(readOpen);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    saveOpen(next);
  };
  return (
    <Box
      data-testid="multicam-how-it-works"
      sx={{ border: `1px solid ${brand.border}`, borderRadius: 2, bgcolor: 'background.paper', mb: 3 }}
    >
      <Button
        onClick={toggle}
        aria-expanded={open}
        aria-controls="multicam-how-it-works-body"
        startIcon={<HelpOutlineOutlinedIcon />}
        endIcon={
          <ExpandMoreOutlinedIcon
            sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }}
          />
        }
        sx={{
          width: '100%',
          justifyContent: 'flex-start',
          px: 2,
          py: 1.25,
          color: 'text.primary',
          fontWeight: 600,
          textTransform: 'none',
          '& .MuiButton-endIcon': { ml: 'auto' },
        }}
      >
        How multicam works
      </Button>
      <Collapse in={open}>
        <Box id="multicam-how-it-works-body" sx={{ px: 2, pb: 2 }}>
          <MulticamHowItWorks />
        </Box>
      </Collapse>
    </Box>
  );
};

interface MulticamHowItWorksDialogProps {
  open: boolean;
  onClose: () => void;
  /** Adds a "Start a multicam set" button when given. */
  onCreate?: () => void;
}

/** The steps in a dialog, for the library's note and the upload panel. */
export const MulticamHowItWorksDialog: React.FC<MulticamHowItWorksDialogProps> = ({ open, onClose, onCreate }) => (
  <Dialog open={open} onClose={onClose} aria-labelledby="multicam-how-title" maxWidth="sm" fullWidth>
    <DialogTitle id="multicam-how-title">How multicam works</DialogTitle>
    <DialogContent>
      <MulticamHowItWorks />
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} variant={onCreate ? 'text' : 'contained'}>
        Close
      </Button>
      {onCreate && (
        <Button
          variant="contained"
          onClick={() => {
            onClose();
            onCreate();
          }}
        >
          Start a multicam set
        </Button>
      )}
    </DialogActions>
  </Dialog>
);

/**
 * Under the video upload: how footage from several cameras goes in, since
 * multicam sets are made from videos already in the library.
 */
export const MulticamUploadTip: React.FC = () => {
  const [open, setOpen] = useState(false);
  return (
    <Box
      data-testid="multicam-upload-tip"
      sx={{
        display: 'flex',
        gap: 1.25,
        alignItems: 'flex-start',
        mt: 2,
        px: 1.75,
        py: 1.5,
        borderRadius: 2,
        border: `1px solid ${brand.border}`,
        bgcolor: brand.ivory,
      }}
    >
      <VideocamOutlinedIcon fontSize="small" aria-hidden="true" sx={{ color: brand.copperDark, mt: '2px' }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Filming on more than one camera?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upload each camera’s recording as its own video, then choose New multicam set in your Library. Files
          that recorded sound line up by themselves, and you can add your recorder’s sound file as you set it up.
        </Typography>
        <Button size="small" onClick={() => setOpen(true)} sx={{ mt: 0.5, ml: -1 }}>
          How multicam works
        </Button>
      </Box>
      <MulticamHowItWorksDialog open={open} onClose={() => setOpen(false)} />
    </Box>
  );
};

/** What to do in the multicam studio, for its help dialog. */
export const MULTICAM_STUDIO_GUIDE: ReadonlyArray<{ title: string; points: string[] }> = [
  {
    title: 'Pick the shots',
    points: [
      'Live cut: every angle plays at once, and the studio opens here. Press 1 to 6 (one key per angle), or tap an angle, to cut to it as it plays. Play a stretch again to redo it, or undo with Ctrl/⌘ Z. The cameras keep running and the sound stays the same: a cut only picks which camera the edit shows, until the next cut.',
      'On an exact frame: pause, step one frame with the , and . keys (or 5 seconds with ← and →), then press the camera’s number. The cut lands on the frame showing.',
      'Review: one angle on screen. Pause where the cut goes, choose Camera cut below the video, pick the angle and choose Add cut. Or press C.',
      'Fix a cut: click it in the list to go there, then choose the pencil to change its time or camera, or the bin to delete it. Or drag it along the timeline’s Cuts lane; Alt with ← or → moves a selected cut one frame.',
      'Suggest cuts (Pro Max): PithPlay listens to each camera’s own sound, works out who’s talking, and suggests a cut to them. Check them in the Suggested lane of the timeline, then Apply or Discard. Undo takes them all back. It works best when each person has their own camera or mic.',
    ],
  },
  {
    title: 'Add notes',
    points: [
      'Pause, choose Note below the video (or press A) and type it. Mark In and Out first (I and O) to cover a stretch. In and Out are only for notes; they don’t make cuts.',
      'In the Transcript tab (Pro Max), select words: drag across them, or click the first word and Shift-click the last. Then choose Remove to take them out of the edit, Short to make them into a short, or Note to say something about them.',
      'Removed words are struck through and shorts are highlighted. Click one to go there, edit it or delete it; Undo puts it back. The note button on a line starts a note with the whole line.',
    ],
  },
  {
    title: 'Hand it off',
    points: [
      'When you’re done, choose Submit for editing.',
      'Your editor gets every angle in sync, your cuts and notes, and a rough cut for Premiere Pro, DaVinci Resolve or Final Cut Pro. On Pro Max, they also get the transcript with what you marked to remove and make into shorts, and a task for each short. Rather not pick the shots? Let PithPlay suggest them (Pro Max), or add notes only and leave the cameras to your editor.',
    ],
  },
];

/** The studio guide's sections, as headed lists. */
export const MulticamStudioGuide: React.FC = () => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }} data-testid="multicam-studio-guide">
    {MULTICAM_STUDIO_GUIDE.map((section) => (
      <Box key={section.title}>
        <Typography variant="subtitle2" component="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
          {section.title}
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'grid', gap: 0.75 }}>
          {section.points.map((point) => (
            <Typography key={point} component="li" variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
              {point}
            </Typography>
          ))}
        </Box>
      </Box>
    ))}
  </Box>
);

export default MulticamHowItWorks;
